import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";

/**
 * Optional type drives which icon is shown in the palette (if any).
 */
export type CommandType = "goto" | "open" | "copy" | "copy-link";

/**
 * A single command shown in the command palette.
 */
export type Command = {
  /**
   * Globally stable identifier. Prefer a semantic id like "open-facility-profile".
   * IDs should not include variable inputs like tenantId; put those in `signature` instead.
   */
  id: string;
  /**
   * Human-readable label rendered in the palette.
   */
  label: string;
  /**
   * Optional secondary text (e.g., target path).
   */
  description?: string;
  /**
   * Action to execute when the command is selected.
   */
  run: () => Promise<void> | void;
  /**
   * Required stability signature used to detect meaningful changes.
   * Engineers must include all relevant inputs that affect this command's output
   * (e.g., tenantId, facilityId, reportId, target path, year, flags).
   * When the signature changes, the registry updates the rendered command.
   */
  signature: string;
  /**
   * Optional type; drives icon in the palette (goto | open | copy).
   */
  type?: CommandType;
  /**
   * Optional custom icon element. When provided, overrides the type-based icon.
   */
  icon?: React.ReactElement;
  /**
   * Optional keywords included in search; not displayed.
   */
  keywords?: string[];
};

export type CommandRegistryContextValue = {
  commands: Command[];
  registerCommands: (sourceKey: string, commands: Command[]) => () => void;
  replaceCommands: (sourceKey: string, commands: Command[]) => void;
  openPalette: () => void;
  openGotoId: (id: string) => void;
};

export const CommandRegistryContext = createContext<CommandRegistryContextValue | null>(null);

/**
 * Register commands with the palette registry.
 * - Registers once on mount and cleans up on unmount
 * - Replaces the command set when any provided dependencies change
 * - No-ops if the registry provider is not present (e.g., non-staff users)
 */
export function useRegisterCommands(
  commands: Command[] | undefined,
  deps: React.DependencyList,
  sourceKey?: string
) {
  const ctx = useContext(CommandRegistryContext);
  const registerCommands = ctx?.registerCommands;
  const replaceCommands = ctx?.replaceCommands;
  const keyRef = useRef<string>(sourceKey ?? `src-${Math.random().toString(36).slice(2)}`);

  // Register once on mount and cleanup on unmount (always register so unmount removes the source)
  useEffect(() => {
    if (!registerCommands) return; // no provider present
    const unregister = registerCommands(keyRef.current, commands ?? []);
    return unregister;
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  // Replace on dependency changes (guarded in provider to avoid loops)
  useEffect(() => {
    if (!replaceCommands) return; // no provider present
    if (!commands) return;
    replaceCommands(keyRef.current, commands);
    // oxlint-disable-next-line exhaustive-deps
  }, deps);

  const update = useCallback(
    (next: Command[]) => {
      if (!replaceCommands) return;
      replaceCommands(keyRef.current, next);
    },
    [replaceCommands]
  );

  return { update };
}
